import React, {
    createContext,
    useContext,
    useEffect,
    useReducer,
    useRef,
    FC,
    ReactNode,
  } from 'react';
  import { TCheckout, CheckoutContextType, CheckoutAction } from '../@types/checkout';
  import { UserContext } from './userContext';
  
  const iState:TCheckout= {
    activeStep: 1,
    paymentOptionId:"",
    orderId:"",
    loginRequired: false,
    cs:"",
    paymentProcessing:false,
    preparingPayment: false,
    postalCode: "",
    addressLocked: false,
    checkoutUser: {
      uict: "",
      username: "",
      email: "",
      firstName: "",
      lastName: ""
    }
  }
  export const CheckoutContext = createContext<CheckoutContextType>({
    checkoutState: iState,
    dispatch: ()=>null,
    setActiveStep: ()=>null,
    setCheckoutUser:()=>null,
    setPaymentOption:()=>null,
    toggleLoginRequired:()=>null,
    setOrderId:()=>null,
    setClientSecret:()=>null,
    setPostalCode:()=>null,
    setAddressLocked:()=>null,
    setPreparingPayment:()=>null,
    togglePaymentProcessing:()=>null
  });
  
  export const userReducer = (checkoutState: TCheckout, action: CheckoutAction): TCheckout => {
    switch (action.type) {
      case 'SET_ACTIVE_STEP':
        return {
          ...checkoutState,
          activeStep: action.payload,
        };
      case 'SET_CHECKOUT_USER':
        return {
          ...checkoutState,
          checkoutUser: action.payload,
        };
      case 'SET_PAYMENT_OPTION':
        return {
          ...checkoutState,
          paymentOptionId: action.payload,
        };
      case 'TOGGLE_LOGIN_REQUIRED':
        return {
          ...checkoutState,
          loginRequired: !checkoutState.loginRequired,
        };
      case 'TOGGLE_PAYMENT_PROCESSING':
        return {
          ...checkoutState,
          paymentProcessing: !checkoutState.paymentProcessing,
        };
      case 'SET_ORDER_ID':
        return {
          ...checkoutState,
          orderId: action.payload,
        };
      case 'SET_CLIENT_SECRET':
        return {
          ...checkoutState,
          cs: action.payload,
        };
      case 'SET_POSTAL_CODE':
        return {
          ...checkoutState,
          postalCode: action.payload,
        };
      case 'SET_ADDRESS_LOCKED':
        return {
          ...checkoutState,
          addressLocked: action.payload,
        };
      case 'SET_PREPARING_PAYMENT':
        return {
          ...checkoutState,
          preparingPayment: action.payload,
        };
      case 'RESET_CHECKOUT':
        return {
          ...iState,
        };

      default:
        return checkoutState;
    }
  };
  
  const CheckoutProvider: FC<{
    children: ReactNode,
    onSetActiveStep?: (payload: TCheckout['activeStep']) => void;
    onSetOrderId?: (payload: TCheckout['orderId']) => void;
    onSetCheckoutUser?: (payload: TCheckout["checkoutUser"])=>void;
    onSetPaymentOption?: (payload: TCheckout["paymentOptionId"])=>void;
    onSetClientSecret?: (payload: TCheckout["cs"])=>void;
    onToggleLoginRequired?: ()=>void;
    onTogglePaymentProcessing?: ()=>void;
}> = ({
    children,
    onSetActiveStep,
    onSetCheckoutUser,
    onSetPaymentOption,
    onSetClientSecret,
    onToggleLoginRequired,
    onTogglePaymentProcessing,
    onSetOrderId
}) => {
    const localState:TCheckout = iState;
    const initialState = useContext(CheckoutContext);
    const [checkoutState, dispatch] = useReducer(userReducer, localState || initialState);

    // CheckoutProvider stays mounted across /cart <-> /checkout navigation
    // (see gatsby-browser.tsx), so cs (a live Stripe client secret), orderId,
    // postalCode, and addressLocked can otherwise survive a login switch that
    // happens without leaving that path range (e.g. guest checkout bouncing
    // to /cart to sign in). Reset on any false->true login transition so a
    // new user never inherits the previous user's in-flight payment intent.
    const { state: userState } = useContext(UserContext);
    const prevIsLoggedIn = useRef(userState.isLoggedIn);
    useEffect(() => {
      if (!prevIsLoggedIn.current && userState.isLoggedIn) {
        dispatch({ type: 'RESET_CHECKOUT' });
      }
      prevIsLoggedIn.current = userState.isLoggedIn;
    }, [userState.isLoggedIn]);

    const setActiveStep = (activeStep: TCheckout['activeStep']) => {
        dispatch({ type: 'SET_ACTIVE_STEP', payload: activeStep });
        onSetActiveStep && onSetActiveStep(activeStep);
      };

    const setOrderId = (orderId: TCheckout['orderId']) => {
        dispatch({ type: 'SET_ORDER_ID', payload: orderId });
        onSetOrderId && onSetOrderId(orderId);
      };

    const setCheckoutUser = (checkoutUser: TCheckout['checkoutUser']) => {
        dispatch({ type: 'SET_CHECKOUT_USER', payload: checkoutUser });
        onSetCheckoutUser && onSetCheckoutUser(checkoutUser);
      };

    const setPaymentOption = (paymentOptionId: TCheckout['paymentOptionId']) => {
        dispatch({ type: 'SET_PAYMENT_OPTION', payload: paymentOptionId });
        onSetPaymentOption && onSetPaymentOption(paymentOptionId);
      };

    const setClientSecret = (cs: TCheckout['cs']) => {
        dispatch({ type: 'SET_CLIENT_SECRET', payload: cs});
        onSetClientSecret && onSetClientSecret(cs);
      };

    const toggleLoginRequired = () => {
      console.log('toggle login required')
        dispatch({ type: 'TOGGLE_LOGIN_REQUIRED'});
        onToggleLoginRequired && onToggleLoginRequired();
      };

    const togglePaymentProcessing = () => {
      console.log('toggle payment processing')
        dispatch({ type: 'TOGGLE_PAYMENT_PROCESSING'});
        onTogglePaymentProcessing && onTogglePaymentProcessing();
      };

    const setPostalCode = (postalCode: string) => {
        dispatch({ type: 'SET_POSTAL_CODE', payload: postalCode });
      };

    const setAddressLocked = (locked: boolean) => {
        dispatch({ type: 'SET_ADDRESS_LOCKED', payload: locked });
      };

    const setPreparingPayment = (loading: boolean) => {
        dispatch({ type: 'SET_PREPARING_PAYMENT', payload: loading });
      };

    return (
      <CheckoutContext.Provider value={{ checkoutState, setActiveStep, setCheckoutUser, setPaymentOption, toggleLoginRequired, togglePaymentProcessing, setOrderId, setClientSecret, setPostalCode, setAddressLocked, setPreparingPayment, dispatch }}>
        {children}
      </CheckoutContext.Provider>
    );
  };
  
  export default CheckoutProvider;
  